import apiClient from './apiClient';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
} from '../../types/api';
import { logger } from '../../lib/logger';

class AuthService {
  /**
   * Регистрация нового пользователя
   * После регистрации пользователь НЕ логинится автоматически
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    // Очищаем данные предыдущего пользователя перед регистрацией
    const currentUser = this.getUser();
    if (currentUser) {
      logger.log('🔄 Clearing localStorage before registration');
      this.logout();
    }

    const response = await apiClient.post<AuthResponse>('/auth/register', data);

    // НЕ сохраняем токен - пользователь должен войти вручную
    // Это стандартный UX flow для безопасности

    return response.data;
  }

  /**
   * Вход в систему
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    logger.log('🔐 authService.login - sending request to API');

    // Проверяем, не логинится ли другой пользователь
    const currentUser = this.getUser();
    const isNewUser = !currentUser || currentUser.email !== data.email;

    if (isNewUser && currentUser) {
      logger.log('🔄 Different user detected, clearing localStorage');
      this.logout(); // Очищаем данные предыдущего пользователя
    }

    const response = await apiClient.post<AuthResponse & { refreshToken?: string }>('/auth/login', data);
    logger.log('✅ API response received');

    // Сохраняем токены и пользователя в localStorage
    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Store refresh token if provided
      if (response.data.refreshToken) {
        localStorage.setItem('refresh_token', response.data.refreshToken);
      }

      logger.log('✅ Tokens and user saved to localStorage');
    }

    return response.data;
  }

  /**
   * Выход из системы
   * Удаляем только auth-related ключи, не трогаем кэш и настройки!
   */
  async logout(): Promise<void> {
    try {
      // Call backend logout to invalidate refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await apiClient.post('/auth/logout', { refreshToken }).catch(() => {
          // Silent fail - we're logging out anyway
        });
      }
    } finally {
      // Удаляем только ключи связанные с авторизацией
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      // НЕ удаляем кэш агентов, настройки темы и прочее!
    }
  }

  /**
   * Синхронный выход (без вызова API)
   * Используется при автоматическом разлогинивании
   */
  logoutSync(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  /**
   * Получить текущего пользователя
   */
  async getCurrentUser(): Promise<{ user: User }> {
    const response = await apiClient.get<{ user: User }>('/auth/me');
    return response.data;
  }

  /**
   * Проверить, авторизован ли пользователь
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  /**
   * Получить токен из localStorage
   */
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Получить пользователя из localStorage
   */
  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Смена пароля текущего пользователя
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  /**
   * Запрос на сброс пароля
   */
  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/auth/forgot-password', {
      email,
    });
    return response.data;
  }

  /**
   * Сброс пароля по токену
   */
  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data;
  }

  /**
   * Загрузить аватар пользователя
   */
  async uploadAvatar(avatarUrl: string): Promise<{ success: boolean; avatarUrl: string }> {
    const response = await apiClient.post<{ success: boolean; avatarUrl: string }>('/auth/avatar', {
      avatarUrl,
    });

    // Обновляем пользователя в localStorage
    const user = this.getUser();
    if (user) {
      user.avatarUrl = response.data.avatarUrl;
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response.data;
  }

  /**
   * Удалить аватар пользователя
   */
  async deleteAvatar(): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>('/auth/avatar');

    // Обновляем пользователя в localStorage
    const user = this.getUser();
    if (user) {
      delete user.avatarUrl;
      localStorage.setItem('user', JSON.stringify(user));
    }

    return response.data;
  }

  /**
   * Обновить профиль пользователя
   */
  async updateProfile(name: string): Promise<{ success: boolean; user: User }> {
    const response = await apiClient.put<{ success: boolean; user: User }>('/auth/profile', {
      name,
    });

    // Обновляем пользователя в localStorage
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  }

  /**
   * Удалить аккаунт пользователя
   */
  async deleteAccount(password: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>('/auth/account', {
      data: { password },
    });

    // Очищаем localStorage после удаления
    this.logout();

    return response.data;
  }

  /**
   * Получить настройки пользователя
   */
  async getSettings(): Promise<UserSettings> {
    const response = await apiClient.get<UserSettings>('/auth/settings');
    return response.data;
  }

  /**
   * Обновить настройки пользователя
   */
  async updateSettings(settings: Partial<UserSettings>): Promise<UserSettings> {
    const response = await apiClient.put<UserSettings>('/auth/settings', settings);
    return response.data;
  }
}

// User settings interface
export interface UserSettings {
  id: string;
  userId: string;
  theme: 'light' | 'dark' | 'system';
  emailNotifications: boolean;
  documentNotifications: boolean;
  marketingNotifications: boolean;
  language: string;
  twoFactorEnabled: boolean;
  templatesFolderId?: string | null;
  templatesFolderName?: string | null;
  generatedDocsFolderId?: string | null;
  generatedDocsFolderName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const authService = new AuthService();
export default authService;
